import { CHARACTER_SCHEMA_VERSION, CharacterV1 } from '@codex/dnd5e/schema';
import { SHEET_KINDS } from '../domain/rows-enums.js';
import { z } from '../zod.js';
import { IsoTimestamp, RowVersion, Uuid } from './common.js';
import { JsonObject } from './documents.js';
import { ActorSheetV1, FIELD_ORIGINS } from './reference.js';
/**
 * The character sheet of Phase 2c (CS r6a deliverable 182; DECISIONS 213 ruling 5): a parallel
 * DTO beside 1.AA's `ActorSheetV1`, so every Phase 1 consumer of the stat block sheet compiles
 * unchanged. `sheets.character.get`, `.list` and `.update` carry it (routes in
 * `routes/sheets-character.ts`, owner 2.I); `sheets.get` on a character answers 409 `wrong_kind`.
 *
 * `fieldOrigins` keys are origin paths on both sheet DTOs (ruling 2): a scalar leaf by its dotted
 * path (`abilities.str.score`, `hp.max`), one key per item by its feature id (`items.<featureId>`);
 * 2.G's `originPaths` names them. `ActorSheetV1`'s shape and `FIELD_ORIGINS` are unchanged.
 */
export { SHEET_KINDS };
/** A key of `actor_sheet.field_origins`: an origin path, as `ActorSheetV1` bounds it. */
export const OriginPath = z.string().min(1).max(500);
/** The D&D Beyond character a sheet came from (`actor_sheet.external_ids`; amendments 2.2 §7). */
export const CharacterExternalIdsV1 = z.strictObject({
    ddb: z
        .strictObject({
        characterId: z.string().min(1).max(64),
        /** Null when the importer's flag holds no url (CS r6b, DECISIONS 802.2; additive). */
        url: z.string().min(1).max(2000).nullable(),
    })
        .optional(),
});
/** The Foundry import a sheet holds (`actor_sheet`'s import columns); null for a sheet never imported. */
export const CharacterFoundryV1 = z.strictObject({
    /** The raw export (`asset` of kind `raw`). */
    rawAssetId: Uuid,
    systemVersion: z.string().min(1).max(64),
    coreVersion: z.string().min(1).max(64),
    /** The SHA-256 of the mapped actor: an equal hash on a refresh writes nothing. */
    importHash: z.string().regex(/^[0-9a-f]{64}$/),
    importedAt: IsoTimestamp,
    /** Foundry's `_stats.modifiedTime` of the imported actor, when the file holds it. */
    modifiedAt: IsoTimestamp.nullable(),
});
/**
 * A character sheet (`actor_sheet` of kind `character`): `ActorSheetV1`'s fields with its
 * `CharacterV1` data, the macro's derived snapshot (`foundrySnapshot`, null when the export had
 * none; DECISIONS 213 ruling 4), the D&D Beyond ids and the Foundry import's stamps.
 */
export const CharacterSheetV1 = ActorSheetV1.extend({
    kind: z.literal('character'),
    schemaVersion: z.literal(CHARACTER_SCHEMA_VERSION),
    data: CharacterV1,
    fieldOrigins: z.record(OriginPath, z.enum(FIELD_ORIGINS)),
    foundrySnapshot: JsonObject.nullable(),
    externalIds: CharacterExternalIdsV1,
    foundry: CharacterFoundryV1.nullable(),
});
/** The most paths one `sheets.character.update` reverts. */
export const REVERT_PATHS_MAX = 500;
/**
 * `sheets.character.update`: a stale `baseVersion` answers 409 `row_conflict`. Each of
 * `revertPaths` takes the latest applied import's value at that origin path, its origin becoming
 * `foundry` (2.H s2's "Revert to Foundry's"; open point 45); a path no applied import holds
 * answers 422 `invalid_body`. A path is named once, and never also changed by `data` in the same
 * request (the server's 422).
 */
export const CharacterSheetUpdateV1 = z.strictObject({
    baseVersion: RowVersion,
    label: z.string().min(1).max(200).nullable().optional(),
    isPrimary: z.boolean().optional(),
    data: CharacterV1.optional(),
    revertPaths: z
        .array(OriginPath)
        .max(REVERT_PATHS_MAX)
        .refine((paths) => new Set(paths).size === paths.length, {
        message: 'Each path is reverted once',
    })
        .optional(),
});
/**
 * `sheets.list`'s query (open point 44): `kind` takes `statblock` only, also the default, so the
 * list's answer stays `ActorSheetV1`; a document's character sheets are `sheets.character.list`.
 */
export const SheetListQueryV1 = z.strictObject({ kind: z.literal('statblock').optional() });
/** `details` of 409 `wrong_kind`: the kind of the sheet the route named. */
export const WrongKindDetailsV1 = z.strictObject({ kind: z.enum(SHEET_KINDS) });
//# sourceMappingURL=sheets-character.js.map